const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const timeOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

const toDate = (value: string | Date) => {
  return value instanceof Date ? value : new Date(value);
};

export const formatLocalDate = (value: string | Date) => {
  return toDate(value).toLocaleDateString(undefined, dateOptions);
};

export const formatLocalTime = (value: string | Date) => {
  return toDate(value).toLocaleTimeString(undefined, timeOptions);
};

export const formatLocalDateTime = (value: string | Date) => {
  return `${formatLocalDate(value)} ${formatLocalTime(value)}`;
};

export const formatRelativeTime = (value: string | Date): string => {
  const now = Date.now();
  const diff = now - toDate(value).getTime();
  const seconds = Math.floor(diff / 1000);

  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

export const formatLocalizedRelativeTime = (
  value: string | Date,
  locale: string,
  options: { now?: number; justNow?: string; fallback?: string } = {},
): string => {
  const date = toDate(value);
  const timestamp = date.getTime();

  if (Number.isNaN(timestamp)) {
    return options.fallback ?? '';
  }

  const now = options.now ?? Date.now();
  const diffMs = timestamp - now;
  const absDiffMs = Math.abs(diffMs);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const monthMs = 30 * dayMs;
  const yearMs = 365 * dayMs;

  if (absDiffMs < minuteMs) {
    return options.justNow ?? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'second');
  }

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', yearMs],
    ['month', monthMs],
    ['day', dayMs],
    ['hour', hourMs],
    ['minute', minuteMs],
  ];
  const [unit, unitMs] = units.find(([, threshold]) => absDiffMs >= threshold) ?? ['minute', minuteMs];
  const amount = Math.trunc(diffMs / unitMs);

  return new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(amount, unit);
};

export const formatSmartDateTime = (value: string | Date) => {
  const date = toDate(value);
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  return isToday
    ? formatLocalTime(date)
    : `${date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })} ${formatLocalTime(date)}`;
};
